/* Gnagfri-only optimistic cart state. Native server responses remain authoritative. */
(function (scope) {
  'use strict';
  const text = value => scope.pepnixText ? scope.pepnixText(value) : value;
  const copy = value => JSON.parse(JSON.stringify(value));
  const properties = value => JSON.stringify(Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b)));
  const signature = item => [String(item.variant_id || item.id), properties(item.properties), String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || '')].join('|');
  const quantityOf = value => Math.max(0, Math.floor(Number(value) || 0));
  function totals(cart, items) {
    const before = cart.items.reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
    const after = items.reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
    return { ...cart, items, item_count: items.reduce((sum, item) => sum + item.quantity, 0), total_price: Math.max(0, Number(cart.total_price || 0) - before + after) };
  }
  class GnagfriCartController {
    constructor({ initialCart, transport, onChange = () => {}, onCheckout = () => {} }) {
      this.confirmed = copy(initialCart || { items: [], item_count: 0, total_price: 0, currency: scope.PepnixLocale?.currency || 'SEK' });
      this.transport = transport; this.onChange = onChange; this.onCheckout = onCheckout;
      this.adds = []; this.targets = new Map(); this.aliases = new Map(); this.running = false;
      this.revision = 0; this.confirmedAt = initialCart ? Date.now() : 0; this.readPromise = null;
      this.checkoutQueued = false; this.error = null; this.waiters = []; this.sequence = 0;
      this.emit();
    }
    resolveKey(key) { const visited = new Set(); while (this.aliases.has(key) && !visited.has(key)) { visited.add(key); key = this.aliases.get(key); } return key; }
    snapshot() {
      let cart = copy(this.confirmed);
      for (const add of this.adds) {
        const item = add.item; const items = cart.items.map(line => ({ ...line }));
        const existing = items.find(line => signature(line) === signature(item));
        const amount = quantityOf(item.quantity || 1), unit = Number(item.final_price ?? item.price ?? 0);
        if (existing) { existing.quantity += amount; existing.final_line_price += unit * amount; existing.original_line_price = (existing.original_line_price || 0) + unit * amount; }
        else items.push({ ...copy(item), key: add.key, id: item.variant_id || item.id, variant_id: item.variant_id || item.id, quantity: amount, product_title: item.product_title || item.title || '', title: item.title || item.product_title || '', final_price: unit, final_line_price: unit * amount, original_line_price: unit * amount });
        cart = totals(cart, items);
      }
      for (const [rawKey, target] of this.targets) {
        const key = this.resolveKey(rawKey), items = cart.items.map(item => ({ ...item }));
        const line = items.find(item => item.key === key); if (!line) continue;
        if (!target) cart = totals(cart, items.filter(item => item.key !== key));
        else { const oldQuantity = line.quantity; line.quantity = target; line.final_line_price = Math.round(line.final_line_price / oldQuantity * target); line.original_line_price = Math.round((line.original_line_price || line.final_line_price) / oldQuantity * target); cart = totals(cart, items); }
      }
      return cart;
    }
    emit() { this.onChange(this.snapshot(), { pending: this.running || this.adds.length > 0 || this.targets.size > 0, error: this.error, checkoutQueued: this.checkoutQueued }); }
    add(item) {
      this.error = null; this.revision++;
      const key = `gnagfri-pending-${++this.sequence}`;
      const promise = new Promise((resolve, reject) => this.adds.push({ key, item: copy(item), resolve, reject }));
      this.emit(); void this.drain(); return promise;
    }
    setQuantity(rawKey, quantity) {
      const key = this.resolveKey(rawKey);
      if (!this.snapshot().items.some(item => item.key === key)) return this.idle();
      this.error = null; this.revision++; this.targets.set(key, quantityOf(quantity));
      this.emit(); void this.drain(); return this.idle();
    }
    rekey(previous, next) {
      for (const [key, target] of [...this.targets]) {
        if (next.items.some(item => item.key === key)) continue;
        const old = previous.items.find(item => item.key === key) || this.adds.find(add => add.key === key)?.item;
        const candidates = old ? next.items.filter(item => signature(item) === signature(old)) : [];
        if (candidates.length === 1) { this.targets.delete(key); this.targets.set(candidates[0].key, target); this.aliases.set(key, candidates[0].key); }
        else if (!target && !candidates.length) this.targets.delete(key);
        else throw new Error(text('Varukorgen ändrades. Kontrollera antal och försök igen.'));
      }
    }
    accept(cart) { const previous = this.confirmed; this.rekey(previous, cart); this.confirmed = copy(cart); this.confirmedAt = Date.now(); this.revision++; }
    async drain() {
      if (this.running) return;
      this.running = true; this.emit();
      try {
        while (this.adds.length || this.targets.size) {
          const addition = this.adds[0];
          if (addition) {
            const next = await this.transport.add(copy(addition.item));
            // Rekey a pending line before removing the corresponding addition.
            this.accept(next); this.adds.shift(); addition.resolve(copy(next)); this.emit(); continue;
          }
          const [key, target] = this.targets.entries().next().value;
          const current = this.confirmed.items.find(item => item.key === key);
          if (!current || current.quantity === target) { this.targets.delete(key); continue; }
          const next = await this.transport.change(key, target);
          // Preserve a newer desired quantity received during the request.
          if (this.targets.get(key) === target) this.targets.delete(key);
          this.accept(next); this.emit();
        }
      } catch (error) {
        this.error = error instanceof Error ? error : new Error(String(error)); this.checkoutQueued = false;
        const abandoned = this.adds.splice(0); this.targets.clear(); this.revision++;
        abandoned.forEach(add => add.reject(this.error)); this.emit();
        // A failed response can follow a successful server write. Read once, never repeat the add.
        const revision = this.revision;
        try { const cart = await this.transport.read(); if (revision === this.revision) { this.confirmed = copy(cart); this.confirmedAt = Date.now(); this.revision++; } } catch (_) { this.confirmedAt = 0; }
      } finally {
        this.running = false; this.emit();
        if (this.adds.length || this.targets.size) { void this.drain(); return; }
        this.waiters.splice(0).forEach(resolve => resolve(this.snapshot()));
        if (this.checkoutQueued) {
          const proceed = !this.error && this.confirmed.item_count > 0;
          this.checkoutQueued = false; this.emit();
          if (proceed) this.onCheckout();
        }
      }
    }
    async refresh(force = false) {
      if (!force && this.confirmedAt && Date.now() - this.confirmedAt < 10000) return this.snapshot();
      if (this.readPromise) return this.readPromise;
      const revision = this.revision;
      this.readPromise = (async () => { try { const cart = await this.transport.read(); if (revision === this.revision && !this.running) { this.accept(cart); this.error = null; this.emit(); } return this.snapshot(); } catch (error) { if (revision === this.revision && !this.running) { this.error = error; this.confirmedAt = 0; this.emit(); } return this.snapshot(); } finally { this.readPromise = null; } })();
      return this.readPromise;
    }
    checkout() {
      if ((this.error && this.running) || !this.snapshot().item_count || this.checkoutQueued) return;
      this.checkoutQueued = true; this.emit();
      if (this.running || this.adds.length || this.targets.size) return;
      if (!this.confirmedAt) {
        // An uncertain write followed by a failed read must never reach checkout unverified.
        void this.refresh(true).then(() => {
          if (!this.checkoutQueued) return;
          if (this.running || this.adds.length || this.targets.size) return;
          const proceed = this.confirmedAt > 0 && !this.error && this.confirmed.item_count > 0;
          this.checkoutQueued = false; this.emit();
          if (proceed) this.onCheckout();
        });
        return;
      }
      this.onCheckout();
    }

    idle() { return this.running || this.adds.length || this.targets.size ? new Promise(resolve => this.waiters.push(resolve)) : Promise.resolve(this.snapshot()); }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = GnagfriCartController;
  if (scope) scope.GnagfriCartController = GnagfriCartController;
})(typeof window === 'undefined' ? null : window);
